<template>
    <div>
        <EditFolderComponent
            :key="`edit-folder-${editingFolder?.slug}`"
            v-if="editingFolder !== null && editingFolder?.slug != 'trash'"
            :gallery-slug="currentSlug"
            :open="showEditFolderForm"
            @close="closeEditFolder"
            @folderDeleted="handleFolderDeleted"
            :folder="editingFolder" />

        <div class="lg:grid lg:grid-cols-12 lg:gap-x-5">
            <ControlsMenuComponent class="sm:mb-6" />


            <aside class="px-0 py-3 sm:px-6 lg:col-span-3 lg:px-0 lg:py-0">
                <PeopleBlockComponent
                    v-if="showPeopleBlock"
                    :gallery-slug="currentSlug"
                    :selected-person="selectedPerson"
                    @select-person="selectPerson"
                />
                <PeoplePromoBlockComponent
                    v-else-if="showPeoplePromoBlock"
                    :available="gallery.face_grouping_available === true"
                />
                <FoldersListComponent
                    v-if="isCurrentGallery"
                    @selectFolder="selectFolder"
                    @edit-folder="handleEditFolder"
                    :selected-folder="selectedFolder"
                    :gallery-slug="currentSlug"
                />
            </aside>

            <div class="space-y-6 sm:px-6 lg:col-span-9 lg:px-0 h-full" v-if="isCurrentGallery">
                <PhotoUploaderV3Component
                    v-if="(!selectedFolder || (selectedFolder && selectedFolder.slug !== 'trash')) && user?.config?.is_uses_new_uploading"
                    :folder="selectedFolder || undefined"
                    ref="uploaderV3"
                />
                <NewPhotoUploaderComponent
                    v-if="(!selectedFolder || (selectedFolder && selectedFolder.slug !== 'trash')) && !user?.config?.is_uses_new_uploading && !selectedPerson"
                    :folder="selectedFolder || undefined"
                />

                <div class="mb-0 z-100 rounded-lg bg-gray-100">
                    <div class="flex flex-col sm:hidden w-full">
                        <button
                            v-if="user?.config?.is_uses_new_uploading && (!selectedFolder || selectedFolder?.slug !== 'trash')"
                            @click.prevent="uploaderV3Click"
                            type="button"
                            class="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 mb-2 text-sm font-medium text-gray-700 bg-white/70 hover:bg-white transition-colors cursor-pointer border border-dashed border-gray-300 hover:border-gray-400 rounded-md"
                        >
                            <ArrowUpTrayIcon class="size-4" />
                            {{ $t('photo_uploader.upload_files') }}
                        </button>

                        <div class="inline-flex justify-between items-center w-full">
                        <span @click.prevent="handleEditFolder(selectedFolder)" class="inline-flex items-center gap-1 text-gray-700 px-1 text-sm font-medium whitespace-nowrap" v-if="selectedPerson === null && selectedFolder !== null && selectedFolder?.slug != 'trash'">
                            {{ $t('files.scene_files') }}: <span class="font-semibold text-gray-800">{{ selectedFolder.title }}</span>
                            <PencilSquareIcon class="shrink-0 size-4 text-royal-blue-600 cursor-pointer" />
                        </span>
                        <span class="text-gray-700 px-1 text-sm font-medium whitespace-nowrap" v-if="selectedPerson === null && selectedFolder === null">{{ $t('files.all_files') }}</span>
                        <span class="text-gray-700 px-1 text-sm font-medium whitespace-nowrap" v-if="selectedPerson === null && selectedFolder && selectedFolder?.slug == 'trash'">{{ $t('files.trashed_files') }}</span>
                        <span class="text-gray-700 px-1 text-sm font-medium whitespace-nowrap" v-if="selectedPerson !== null">{{ $t('people_block.person_photos') }}</span>

                        <Menu as="div" class="relative inline-block text-left ml-auto mr-2">
                            <div>
                                <MenuButton class="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-2 py-1.5 text-sm font-semibold text-gray-900 shadow-xs hover:bg-gray-50 cursor-pointer">
                                    <BarsArrowDownIcon class="size-5 text-gray-400" aria-hidden="true" />
                                    <ChevronDownIcon20 class="-mr-1 size-5 text-gray-400 hidden" aria-hidden="true" />
                                </MenuButton>
                            </div>

                            <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                                <MenuItems class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                                    <div class="py-1">
                                        <MenuItem v-for="option in sortOptions" :key="option.value">
                                            <button
                                                type="button"
                                                :disabled="option.value === 'manual'"
                                                @click="order_by = option.value"
                                                :class="[
                                                            order_by == option.value ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700',
                                                            option.value === 'manual' ? 'cursor-default' : 'cursor-pointer',
                                                            'hover:bg-gray-50 block px-4 py-2 text-sm text-left w-full'
                                                        ]">
                                                {{ option.name }}
                                            </button>
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </transition>
                        </Menu>

                        <div class="items-center rounded-md bg-white p-0.5 flex gap-0.5">
                            <button
                                @click.prevent="layout = 'list'"
                                type="button"
                                :class="['rounded-md p-1 cursor-pointer transition-all duration-150', layout === 'list' ? 'bg-gray-100 text-gray-600' : 'text-gray-400 hover:text-gray-600']">
                                <Bars4Icon class="size-5" aria-hidden="true" />
                                <span class="sr-only">Use list view</span>
                            </button>
                            <button
                                @click.prevent="layout = 'grid'"
                                type="button"
                                :class="['rounded-md p-1 cursor-pointer transition-all duration-150', layout === 'grid' ? 'bg-gray-100 text-gray-600' : 'text-gray-400 hover:text-gray-600']">
                                <Squares2X2IconMini class="size-5" aria-hidden="true" />
                                <span class="sr-only">Use grid view</span>
                            </button>
                        </div>
                        </div><!-- end folder name + sort + view row -->
                    </div><!-- end mobile flex-col -->
                    <div class="hidden sm:block">
                        <div class="flex items-center border-b border-gray-200">
                            <nav class="-mb-px flex flex-1 space-x-6 xl:space-x-8">
                                <a href="#" @click.prevent="handleEditFolder(selectedFolder)" class="inline-flex items-center gap-1 border-transparent text-gray-700 border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap" v-if="selectedPerson === null && selectedFolder !== null && selectedFolder?.slug != 'trash'">
                                    {{ $t('files.scene_files') }}: <span class="font-semibold text-gray-800">{{ selectedFolder.title }}</span>
                                    <PencilSquareIcon class="shrink-0 size-4 text-royal-blue-600 cursor-pointer" />
                                </a>
                                <a href="#" class="border-transparent text-gray-700 border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap" v-if="selectedPerson === null && selectedFolder === null">{{ $t('files.all_files') }}</a>
                                <a href="#" class="border-transparent text-gray-700 border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap" v-if="selectedPerson === null && selectedFolder && selectedFolder?.slug == 'trash'">{{ $t('files.trashed_files') }}</a>
                                <a href="#" @click.prevent="selectPerson(null)" class="inline-flex items-center gap-1 border-transparent text-gray-700 border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap" v-if="selectedPerson !== null">
                                    {{ $t('people_block.person_photos') }}
                                </a>
                            </nav>
                            <button
                                v-if="user?.config?.is_uses_new_uploading && (!selectedFolder || selectedFolder?.slug !== 'trash')"
                                @click.prevent="uploaderV3Click"
                                type="button"
                                class="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 bg-white/70 border border-dashed border-gray-300 hover:border-gray-400 hover:bg-white transition-colors cursor-pointer"
                            >
                                <ArrowUpTrayIcon class="size-4" />
                                {{ $t('photo_uploader.upload_files') }}
                            </button>

                            <div class="ml-2 hidden items-center gap-2 sm:flex">
                                <Menu as="div" class="relative inline-block text-left">
                                    <div>
                                        <MenuButton class="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-2 py-1.5 text-sm font-semibold text-gray-900 shadow-xs hover:bg-gray-50 cursor-pointer">
                                            <BarsArrowDownIcon class="size-5 text-gray-400" aria-hidden="true" />
                                            <ChevronDownIcon20 class="-mr-1 size-5 text-gray-400 hidden" aria-hidden="true" />
                                        </MenuButton>
                                    </div>

                                    <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                                        <MenuItems class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                                            <div class="py-1">
                                                <MenuItem v-for="option in sortOptions" :key="option.value">
                                                    <button
                                                        type="button"
                                                        :disabled="option.value === 'manual'"
                                                        @click="order_by = option.value"
                                                        :class="[
                                                            order_by == option.value ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700',
                                                            option.value === 'manual' ? 'cursor-default' : 'cursor-pointer',
                                                            'hover:bg-gray-50 block px-4 py-2 text-sm text-left w-full'
                                                        ]">
                                                        {{ option.name }}
                                                    </button>
                                                </MenuItem>
                                            </div>
                                        </MenuItems>
                                    </transition>
                                </Menu>

                                <div class="items-center rounded-md bg-white p-0.5 flex gap-0.5">
                                    <button
                                        @click.prevent="layout = 'list'"
                                        type="button"
                                        :class="['rounded-md p-1 cursor-pointer transition-all duration-150', layout === 'list' ? 'bg-gray-100 text-gray-600' : 'text-gray-400 hover:text-gray-600']">
                                        <Bars4Icon class="size-5" aria-hidden="true" />
                                        <span class="sr-only">Use list view</span>
                                    </button>
                                    <button
                                        @click.prevent="layout = 'grid'"
                                        type="button"
                                        :class="['rounded-md p-1 cursor-pointer transition-all duration-150', layout === 'grid' ? 'bg-gray-100 text-gray-600' : 'text-gray-400 hover:text-gray-600']">
                                        <Squares2X2IconMini class="size-5" aria-hidden="true" />
                                        <span class="sr-only">Use grid view</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <PhotosListComponent
                    v-if="layout === 'grid'"
                    @orderByUpdated="orderByUpdated"
                    @photo-moved="handlePhotoMoved"
                    :layout="gallery.design.layout"
                    :small-grid="smallGrid"
                    :folder="selectedFolder || undefined" />

                <PhotosListTableComponent
                    v-if="layout === 'list'"
                    @orderByUpdated="orderByUpdated"
                    @photo-moved="handlePhotoMoved"
                    :layout="gallery.design.layout"
                    :small-grid="smallGrid"
                    :folder="selectedFolder || undefined" />
            </div>
        </div>

    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

import {Folder, Gallery} from "@/types/GalleryType";
import {validationMethods} from "@/_helpers/validation";
import {mapActions, mapGetters} from "vuex";

import ControlsMenuComponent from "@/components/Galleries/ControlsMenuComponent.vue";
import PhotosListComponent from "@/components/Galleries/PhotosListComponent.vue";
import NewPhotoUploaderComponent from "@/components/Galleries/NewPhotoUploaderComponent.vue";
import PhotoUploaderV3Component from "@/components/Galleries/PhotoUploaderV3Component.vue";
import FoldersListComponent from "@/components/Galleries/Folders/FoldersListComponent.vue";
import PeopleBlockComponent from "@/components/Galleries/Folders/PeopleBlockComponent.vue";
import PeoplePromoBlockComponent from "@/components/Galleries/Folders/PeoplePromoBlockComponent.vue";
import {FacePerson} from "@/types/FaceGroupingType";
import {isSafari} from "@/_helpers/functions";

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import {
    Bars4Icon,
    Squares2X2Icon as Squares2X2IconMini,
    ChevronDownIcon as ChevronDownIcon20,
    PencilSquareIcon,
    ArrowUpTrayIcon,
} from '@heroicons/vue/20/solid'
import { BarsArrowDownIcon } from '@heroicons/vue/16/solid'
import {formatStorage} from "@/_helpers/formatters";
import PhotosListTableComponent from "@/components/Galleries/Photos/PhotosListTableComponent.vue";
import EditFolderComponent from "@/components/Galleries/Folders/EditFolderComponent.vue";

export default defineComponent({
    name: "PhotosPage",

    components: {
        EditFolderComponent,
        PhotosListTableComponent,
        PhotosListComponent,
        // PhotoItemLoaderComponent,
        FoldersListComponent,
        PeopleBlockComponent,
        PeoplePromoBlockComponent,
        NewPhotoUploaderComponent,
        PhotoUploaderV3Component,
        ControlsMenuComponent,

        Menu, MenuButton, MenuItem, MenuItems,

        Bars4Icon,
        ChevronDownIcon20,
        BarsArrowDownIcon,
        ArrowUpTrayIcon,
        Squares2X2IconMini,
        PencilSquareIcon
    },

    data() {
        return {
            isLoadingGallery: false,
            isLoadingFolders: false,

            form: {} as Gallery,

            errors: {} as { [key: string]: string[] },

            selectedFolder: null as Folder | null,
            selectedPerson: null as FacePerson | null,

            showEditFolderForm: false,
            editingFolder: null as Folder | null,

            smallGrid: false,

            layout: localStorage.getItem('gallery-photos-layout') || 'grid',

            sortOptions: [
                {
                    name: this.$t('files.sort.abc'),
                    value: 'abc',
                },
                {
                    name: this.$t('files.sort.reverse_abc'),
                    value: 'reverse_abc',
                },
                {
                    name: this.$t('files.sort.oldest'),
                    value: 'oldest',
                },
                {
                    name: this.$t('files.sort.random'),
                    value: 'random',
                },
                {
                    name: this.$t('files.sort.manual'),
                    value: 'manual',
                }
            ],
        };
    },

    computed: {
        ...mapGetters({
            gallery: 'galleries/gallery',
            folders: 'galleries/folders',

            photos: 'photos/photos',

            isLoadingPhotos: 'photos/isLoadingList',

            user: 'user/user',
        }),

        currentSlug(): string {
            return this.$route.params.gallery as string;
        },

        isCurrentGallery(): boolean {
            return this.gallery != null && this.gallery.slug == this.currentSlug;
        },

        hasPhotos(): boolean {
            return this.isCurrentGallery && (this.gallery.photos_count ?? 0) > 0;
        },

        showPeopleBlock(): boolean {
            return this.hasPhotos
                && this.gallery.face_grouping_available === true
                && this.gallery.face_grouping_enabled === true;
        },

        showPeoplePromoBlock(): boolean {
            if (!this.hasPhotos) return false;
            if (this.showPeopleBlock) return false;
            if (!this.user?.config?.face_grouping_visible) return false;
            return true;
        },

        order_by: {
            get(): string {
                if(this.selectedFolder) {
                    return this.selectedFolder.order_by;
                }

                return this.gallery.order_by;
            },
            set(value: string): void {
                this.sortPhotos({
                    photos: this.photos,
                    gallery: this.gallery.slug,
                    type: value,
                    folder: this.selectedFolder?.slug
                }).then(() => {
                    this.fetchPhotos(false);
                });
            }
        },

        isSafari(): Boolean {
            return isSafari()
        }
    },

    watch: {
        layout: {
            handler(newLayout) {
                localStorage.setItem('gallery-photos-layout', newLayout);
            },
            immediate: true
        }
    },

    mounted() {
        this.fetchGallery();
    },

    methods: {
        formatStorage,

        uploaderV3Click() {
            (this.$refs.uploaderV3 as any)?.clickSelectFiles();
        },
        ...mapActions({
            loadGallery: "galleries/loadGallery",
            loadFolders: "galleries/loadFolders",

            loadPhotos: "photos/loadPhotos",
            sortPhotos: "photos/sortPhotos",

            errorAlert: 'alerts/error',
        }),

        ...validationMethods(),

        fetchGallery() {
            if(this.isCurrentGallery) {
                this.form = { ...this.gallery };

                this.fetchFolders();
                this.fetchPhotos();

                return;
            }

            if(!this.isCurrentGallery) {
                this.isLoadingGallery = true;
            }

            this.loadGallery({slug: this.currentSlug})
                .then((gallery: Gallery) => {
                    this.form = { ...gallery };

                    this.fetchFolders();
                    this.fetchPhotos();

                    this.isLoadingGallery = false;
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: this.$t('files.error.gallery', {
                            message: data.message,
                        }),
                    });

                    this.isLoadingGallery = false;
                });
        },

        fetchFolders() {
            this.isLoadingFolders = true;

            this.loadFolders({gallery: this.currentSlug})
                .then((folders) => {
                    const selectedFolderSlug = (this.$route.hash).replace('#', '');

                    const selectedFolder = folders.find((item: Folder) => item.slug == selectedFolderSlug);

                    if(selectedFolder) {
                        this.selectFolder(selectedFolder);
                    }

                    this.isLoadingFolders = false;
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: this.$t('files.error.folders', {
                            message: data.message,
                        }),
                    });

                    this.isLoadingFolders = false;
                });
        },

        selectFolder(folder: Folder | null) {
            this.selectedFolder = folder;
            this.selectedPerson = null;

            this.fetchPhotos();

            location.hash = '';
        },

        selectPerson(person: FacePerson | null) {
            this.selectedPerson = person;
            if (person !== null) {
                this.selectedFolder = null;
            }

            this.fetchPhotos();

            location.hash = '';
        },

        handleEditFolder(folder: Folder) {
            this.editingFolder = folder;
            this.showEditFolderForm = true;
        },

        closeEditFolder() {
            this.showEditFolderForm = false;
            this.editingFolder = null;
        },

        handleFolderDeleted(folderSlug: string) {
            console.log('Folder deleted:', folderSlug);
            // Скидаємо вибрану папку, щоб показати загальний список фото
            this.selectedFolder = null;
            // Закриваємо форму редагування
            this.closeEditFolder();
            // Оновлюємо список фото (тепер без папки)
            this.fetchPhotos();
        },

        fetchPhotos(withLoadingStatus = true) {
            const cluster: string | number | null = this.selectedPerson !== null
                ? (this.selectedPerson as FacePerson).id
                : null;

            this.loadPhotos({
                gallery: this.currentSlug,
                folder: this.selectedFolder?.slug,
                cluster,
                withLoadingStatus
            })
                .then((photos) => {
                    console.log(photos);
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: this.$t('files.error.files', {
                            message: data.message,
                        }),
                    });
                });
        },

        handlePhotoMoved() {
            // Оновлюємо список фото після переміщення
            this.fetchPhotos(false);
        },

        orderByUpdated() {
            // Обробка оновлення порядку сортування
            this.fetchPhotos(false);
        },

    }
})
</script>

<style lang="scss" scoped>

</style>